import axios from "axios";
import { buildApiUrl, encodePathValue, headersRequest } from "../../helpers";

export const BriefcaseServices = {
  // PickList filtrable: tipo / pais / metodologia / propietario.
  getDataByFilter: async (data = {}) => {
    const params = new URLSearchParams({
      tipo: data.tipologia ?? "undefined",
      pais: data.country ?? "undefined",
      metodologia: data.methodology ?? "undefined",
      propietario: data.owner ?? "undefined",
    });
    const requestUrl = buildApiUrl(`briefcase-filter?${params.toString()}`);
    const result = await axios.get(requestUrl, headersRequest(requestUrl));
    return result.data;
  },

  // Lista de portafolios de la organización.
  list: async () => {
    const requestUrl = buildApiUrl("briefcases");
    const result = await axios.get(requestUrl, headersRequest(requestUrl));
    return result.data;
  },

  // Detalle de un portafolio + proyectos asociados.
  getById: async (uid) => {
    const requestUrl = buildApiUrl(`briefcases/${encodePathValue(uid)}`);
    const result = await axios.get(requestUrl, headersRequest(requestUrl));
    return result.data;
  },

  // Crear portafolio.
  create: async (payload) => {
    const requestUrl = buildApiUrl("briefcases");
    const result = await axios.post(
      requestUrl,
      payload,
      headersRequest(requestUrl)
    );
    return result.data;
  },

  // Editar portafolio + reconciliar proyectos asociados.
  update: async (uid, payload) => {
    const requestUrl = buildApiUrl(`briefcases/${encodePathValue(uid)}`);
    const result = await axios.put(
      requestUrl,
      payload,
      headersRequest(requestUrl)
    );
    return result.data;
  },

  // Eliminar portafolio.
  remove: async (uid) => {
    const requestUrl = buildApiUrl(`briefcases/${encodePathValue(uid)}`);
    const result = await axios.delete(requestUrl, headersRequest(requestUrl));
    return result.data;
  },
};

// Artefactos del portafolio (F2): riesgos, compromisos y novedades. Las 3 matrices son simétricas
// (list/create/update/remove anidados bajo /briefcases/:uid/<matriz>). Construye un set de métodos
// CRUD parametrizado por nombre de matriz para no triplicar la maquinaria HTTP. Sin concatenación cruda:
// siempre buildApiUrl + encodePathValue + headersRequest.
const buildArtifactService = (matriz) => ({
  list: async (idPortafolio) => {
    const requestUrl = buildApiUrl(
      `briefcases/${encodePathValue(idPortafolio)}/${matriz}`
    );
    const result = await axios.get(requestUrl, headersRequest(requestUrl));
    return result.data;
  },
  create: async (idPortafolio, payload) => {
    const requestUrl = buildApiUrl(
      `briefcases/${encodePathValue(idPortafolio)}/${matriz}`
    );
    const result = await axios.post(requestUrl, payload, headersRequest(requestUrl));
    return result.data;
  },
  update: async (idPortafolio, idItem, payload) => {
    const requestUrl = buildApiUrl(
      `briefcases/${encodePathValue(idPortafolio)}/${matriz}/${encodePathValue(idItem)}`
    );
    const result = await axios.put(requestUrl, payload, headersRequest(requestUrl));
    return result.data;
  },
  remove: async (idPortafolio, idItem) => {
    const requestUrl = buildApiUrl(
      `briefcases/${encodePathValue(idPortafolio)}/${matriz}/${encodePathValue(idItem)}`
    );
    const result = await axios.delete(requestUrl, headersRequest(requestUrl));
    return result.data;
  },
});

export const RiesgosServices = buildArtifactService("riesgos");
export const CompromisosServices = buildArtifactService("compromisos");
export const NovedadesServices = buildArtifactService("novedades");
export const AprendizajesServices = buildArtifactService("aprendizajes");
export const RetrospectivasServices = buildArtifactService("retrospectivas");

// DDL de compromisos (Criticidad/Estado/Responsable/Líder). Scope por organización del usuario en backend.
CompromisosServices.getCompromisosDdl = async (idPortafolio) => {
  const requestUrl = buildApiUrl(
    `briefcases/${encodePathValue(idPortafolio)}/compromisos-ddl`
  );
  const result = await axios.get(requestUrl, headersRequest(requestUrl));
  return result.data;
};

// DDL de aprendizajes (Tipo: TIPO_APRENDIZAJE). Scope por organización del usuario en backend.
AprendizajesServices.getAprendizajesDdl = async (idPortafolio) => {
  const requestUrl = buildApiUrl(
    `briefcases/${encodePathValue(idPortafolio)}/aprendizajes-ddl`
  );
  const result = await axios.get(requestUrl, headersRequest(requestUrl));
  return result.data;
};

// Lista de aprendizajes para el selector "Aprendizaje a reutilizar": TODOS los originales de la
// organización (a diferencia de getAprendizajes/list, que se acota al portafolio del path). El
// backend (SP_PORTAFOLIO_APRENDIZAJES_LISTAR_REUTILIZACION) filtra por IdOrganizacion y excluye las
// reutilizaciones; el :uid del path no se usa para acotar, va solo por consistencia de la ruta.
AprendizajesServices.getAprendizajesReutilizacion = async (idPortafolio) => {
  const requestUrl = buildApiUrl(
    `briefcases/${encodePathValue(idPortafolio)}/aprendizajes-reutilizacion`
  );
  const result = await axios.get(requestUrl, headersRequest(requestUrl));
  return result.data;
};
